/**
 * EliminationNotification — HUD element (extracted from PixiHUDRenderer.js in Phase 3D).
 *
 * create(hud) builds the PIXI objects into hud.elements.eliminationNotification,
 * position(hud) lays them out for hud.screenWidth/Height, update(hud, …) redraws
 * from game state. `hud` is the PixiHUDRenderer coordinator: it owns
 * hudContainer, elements, screen size, _createText() and send().
 */
import * as PIXI from 'pixi.js';
import { bannerContent, ELIMINATION_RED } from './eliminationBanner.js';

/**
 * Create elimination notification
 */
export function create(hud) {
  const container = new PIXI.Container();
  container.visible = false;
  hud.hudContainer.addChild(container);

  // Background
  const background = new PIXI.Graphics();
  container.addChild(background);

  // Text
  const baseText = new PIXI.Text({
    text: '',
    style: {
      fontFamily: 'Arial',
      fontSize: 36,
      fontWeight: 'bold',
      fill: 0xffffff
    }
  });
  baseText.anchor.set(0, 0.5);
  container.addChild(baseText);

  const nameText = new PIXI.Text({
    text: '',
    style: {
      fontFamily: 'Arial',
      fontSize: 36,
      fontWeight: 'bold',
      fill: ELIMINATION_RED
    }
  });
  nameText.anchor.set(0, 0.5);
  container.addChild(nameText);

  hud.elements.eliminationNotification = { container, background, baseText, nameText };
}

/**
 * Update elimination notification
 * @param {Object} notification - Notification data
 * @param {Object} callbacks - Callback functions
 */
export function update(hud, notification, callbacks) {
  const en = hud.elements.eliminationNotification;
  if (!en) return;

  if (!notification) {
    en.container.visible = false;
    return;
  }

  const { uiConfig } = callbacks;
  const now = performance.now();
  const timeUntilExpire = notification.expiresAt - now;
  const fadeOutDuration = uiConfig?.eliminationNotification?.fadeOutDuration || 500;

  // Calculate opacity
  let opacity = 1.0;
  if (timeUntilExpire < fadeOutDuration) {
    opacity = Math.max(0, timeUntilExpire / fadeOutDuration);
  }

  en.container.visible = true;
  en.container.alpha = opacity;

  // Update text: a kill in red, a shot decoy mocks the shooter in pink
  const content = bannerContent(notification);
  en.baseText.text = content.base;
  en.nameText.text = content.highlight;
  if (en.nameText.style.fill !== content.highlightColor) en.nameText.style.fill = content.highlightColor;

  // Position at center
  const centerX = hud.screenWidth / 2;
  const centerY = hud.screenHeight / 2 - 100;

  const totalWidth = en.baseText.width + en.nameText.width;
  const padding = 30;
  const blockWidth = totalWidth + padding * 2;
  const blockHeight = 60;

  const startX = centerX - totalWidth / 2;
  en.baseText.x = startX;
  en.baseText.y = centerY;
  en.nameText.x = startX + en.baseText.width;
  en.nameText.y = centerY;

  en.background.clear();
  en.background.rect(centerX - blockWidth / 2, centerY - blockHeight / 2, blockWidth, blockHeight);
  en.background.fill({ color: 0x000000, alpha: 0.5 });
}
